import { useEffect, useState } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useLookupRefresh } from "../../context/LookupContext.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";
import { Input } from "../../components/ui/input.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";

const CATEGORY = "DESIGNATION";

interface DesignationRow { id: string; category: string; value: string; displayName: string; isActive: boolean; isSystem: boolean }

/**
 * Admin → Designations: the list HR picks an employee's designation from on the
 * employee form. Stored as the DESIGNATION lookup category; the employee keeps
 * the display name, so a rename here carries over to everyone who has it.
 */
export default function DesignationsPanel({ canEdit }: { canEdit: boolean }) {
  const refreshLookup = useLookupRefresh();
  const confirm = useConfirm();
  const [rows, setRows] = useState<DesignationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.get<DesignationRow[]>("/admin/lookup");
      setRows(r.data.filter((row) => row.category === CATEGORY).sort((a, b) => a.displayName.localeCompare(b.displayName)));
      setError(null);
    } catch (e) {
      logError("DesignationsPanel.load", e);
      setError(errMsg(e) ?? "Could not load designations.");
    }
  };

  useEffect(() => { void load(); }, []);

  // The value is unique per category but upper-cased by the API, so compare names too.
  const isTaken = (name: string, exceptId?: string) =>
    (rows ?? []).some((r) => r.id !== exceptId && r.displayName.trim().toLowerCase() === name.trim().toLowerCase());

  const afterChange = async () => { await load(); refreshLookup(); };

  async function add() {
    const name = newName.trim();
    if (!name) { setError("Enter a designation name."); throw new Error("validation"); }
    if (isTaken(name)) { setError(`"${name}" is already in the list.`); throw new Error("validation"); }
    try {
      await api.post("/admin/lookup", { category: CATEGORY, value: name, displayName: name });
      setNewName("");
      await afterChange();
    } catch (e) {
      logError("DesignationsPanel.add", e);
      setError(errMsg(e) ?? "Could not add the designation.");
      throw e;
    }
  }

  async function run(id: string, action: () => Promise<unknown>, failure: string) {
    setBusyId(id); setError(null);
    try {
      await action();
      await afterChange();
    } catch (e) {
      logError("DesignationsPanel.update", e);
      setError(errMsg(e) ?? failure);
    } finally {
      setBusyId(null);
    }
  }

  function saveRename(row: DesignationRow) {
    const name = editing?.name.trim() ?? "";
    if (!name) { setError("Enter a designation name."); return; }
    if (name === row.displayName) { setEditing(null); return; }
    if (isTaken(name, row.id)) { setError(`"${name}" is already in the list.`); return; }
    void run(row.id, async () => {
      await api.patch(`/admin/lookup/${row.id}`, { displayName: name });
      setEditing(null);
    }, "Could not rename the designation.");
  }

  async function remove(row: DesignationRow) {
    const ok = await confirm({
      title: `Delete "${row.displayName}"?`,
      description: "It is removed from the list. Employees who already have it keep it until HR changes it.",
      actionText: "Delete",
      tone: "destructive",
    });
    if (ok) void run(row.id, () => api.delete(`/admin/lookup/${row.id}`), "Could not delete the designation.");
  }

  const shown = (rows ?? []).filter((r) => r.displayName.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Designations</CardTitle>
          <CardDescription>
            The options HR picks from for an employee's designation. Renaming one updates every employee who has it; inactive ones are hidden from the dropdown.
          </CardDescription>
        </CardHeader>
        {canEdit && (
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") add().catch(() => {}); }}
                placeholder="e.g. Software Engineer"
                maxLength={100}
                className="w-72"
              />
              <AsyncButton onClick={() => add()} disabled={!newName.trim()}>Add designation</AsyncButton>
            </div>
          </CardContent>
        )}
      </Card>

      {error && <p role="alert" className="text-sm text-[var(--color-error-fg)]">{error}</p>}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-surface-500">{rows ? `${rows.length} designation${rows.length === 1 ? "" : "s"}` : ""}</span>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search designations" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {rows === null ? (
            <p className="p-6 text-sm text-surface-500">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="p-6 text-sm text-surface-500">{rows.length === 0 ? "No designations yet. Add one above." : "No designation matches your search."}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Designation</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="w-px text-right whitespace-nowrap">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {editing?.id === row.id ? (
                        <Input
                          value={editing.name}
                          onChange={(e) => setEditing({ id: row.id, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRename(row); if (e.key === "Escape") setEditing(null); }}
                          maxLength={100}
                          autoFocus
                          className="h-8 w-72"
                        />
                      ) : (
                        <span className="font-medium break-words text-surface-900">{row.displayName}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? "Active" : "Inactive"}</Badge>
                    </TableCell>
                    {canEdit && (
                      // Never wraps or shrinks, so a long designation can't push the buttons out of view.
                      <TableCell className="w-px whitespace-nowrap">
                        <div className="flex justify-end gap-1.5">
                          {editing?.id === row.id ? (
                            <>
                              <Button size="xs" onClick={() => saveRename(row)} disabled={busyId === row.id}>Save</Button>
                              <Button size="xs" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
                            </>
                          ) : (
                            <>
                              <Button size="xs" variant="secondary" onClick={() => setEditing({ id: row.id, name: row.displayName })} disabled={busyId === row.id}>
                                <Pencil size={11} /> Edit
                              </Button>
                              <Button
                                size="xs" variant={row.isActive ? "outline" : "success"} disabled={busyId === row.id}
                                onClick={() => void run(row.id, () => api.patch(`/admin/lookup/${row.id}`, { isActive: !row.isActive }), "Could not change the status.")}
                              >
                                <Power size={11} /> {row.isActive ? "Deactivate" : "Activate"}
                              </Button>
                              {!row.isSystem && (
                                <Button size="xs" variant="destructive" onClick={() => void remove(row)} disabled={busyId === row.id}>
                                  <Trash2 size={11} /> Delete
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
